// Command palette: the ⌘K box in the top bar. Two things in one list, commands
// for getting around and results from the global search endpoint.
//
// Results come back grouped and already tenant-scoped by the server. This file
// does no filtering of its own on the data, so there is one place to get that
// wrong rather than two. It only decides where a row goes when clicked.
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import MDEditor from '@uiw/react-md-editor';
import API_BASE from '../config';

// Workspace screens, reachable by name. `op` marks the ones that live on the
// operator shell and do not need a client.
const NAV = [
  { to: 'home', label: 'Home', terms: 'home dashboard overview' },
  { to: 'contacts', label: 'Contacts', terms: 'contacts people' },
  { to: 'companies', label: 'Companies', terms: 'companies accounts organisations' },
  { to: 'opportunities', label: 'Opportunities', terms: 'opportunities deals pipeline' },
  { to: 'lists', label: 'Lists', terms: 'lists segments' },
  { to: 'inbox', label: 'Inbox', terms: 'inbox replies messages' },
  { to: 'deliverability', label: 'Deliverability', terms: 'deliverability mailboxes domains spf dkim dmarc' },
  { to: 'sequences', label: 'Sequences', terms: 'sequences steps copy' },
  { to: 'campaigns', label: 'Campaigns', terms: 'campaigns sends' },
  { to: 'email-batches', label: 'Email batches', terms: 'email batches' },
  { to: 'linkedin-batches', label: 'LinkedIn batches', terms: 'linkedin batches' },
  { to: 'market', label: 'Market', terms: 'market graph segments offerings' },
  { to: 'intelligence', label: 'Intelligence', terms: 'intelligence digests signals' },
  { to: 'performance', label: 'Performance', terms: 'performance metrics numbers cost' },
  { to: 'profile', label: 'Client profile', terms: 'client profile icp discovery details' },
  { to: 'account', label: 'Account', terms: 'account billing plan' },
  { to: 'usage', label: 'Usage', terms: 'usage credits' },
  { to: 'documents', label: 'Documents', terms: 'documents files agreements' },
  { to: 'webhooks', label: 'Webhooks', terms: 'webhooks events' },
  { to: 'clients', label: 'Clients', terms: 'clients switch', op: true },
  { to: 'attribution', label: 'Attribution', terms: 'attribution signals sending accounts', op: true },
];

export default function CommandPalette({ clientId, isStaff, onClose }) {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const [q, setQ] = useState('');
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [partial, setPartial] = useState(false);
  const [errors, setErrors] = useState([]);
  const [scopeAll, setScopeAll] = useState(!clientId);
  const [sel, setSel] = useState(0);
  const [ask, setAsk] = useState(null);

  const authFetch = useCallback(async (path) => {
    const token = await getToken();
    return fetch(`${API_BASE}${path}`, { headers: { Authorization: `Bearer ${token}` } });
  }, [getToken]);

  useEffect(() => { if (inputRef.current) inputRef.current.focus(); }, []);

  // Tier 1 first so the common things appear immediately, then the full sweep
  // replaces it. Both are the same endpoint, so there is no second code path.
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setGroups([]); setPartial(false); setErrors([]); return; }
    let dead = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      const scope = scopeAll ? 'all' : `client:${clientId}`;
      const base = `/search?q=${encodeURIComponent(term)}&scope=${scope}`;
      try {
        // One request, one round-trip. The two-wave version existed only because
        // the old backend needed thirty-seven trips to answer.
        const r = await authFetch(base);
        if (!dead && r.ok) {
          const j = (await r.json()) || {};
          setGroups(j.groups || []);
          setPartial(false);
          setErrors(j.errors || []);
        }
      } catch (_) {
        if (!dead) setGroups([]);
      } finally {
        if (!dead) setLoading(false);
      }
    }, 180);
    return () => { dead = true; clearTimeout(timer); };
  }, [q, scopeAll, clientId, authFetch]);

  const commands = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return [];
    return NAV
      .filter(n => (n.op ? isStaff : !!clientId))
      .filter(n => n.label.toLowerCase().includes(term) || n.terms.includes(term))
      .slice(0, 5)
      .map(n => ({ kind: 'nav', to: n.to, op: !!n.op, title: n.label, label: 'Go to' }));
  }, [q, clientId, isStaff]);

  // One flat list so the arrow keys move through everything in display order.
  const flat = useMemo(() => {
    const rows = [...commands];
    for (const g of groups) for (const r of g.rows) rows.push({ kind: 'result', ...r });
    if (q.trim().length >= 2 && clientId && !rows.length && !loading) {
      rows.push({ kind: 'raptor', title: `Ask Raptor about "${q.trim()}"`, label: 'Raptor' });
    }
    return rows;
  }, [commands, groups, q, clientId, loading]);

  useEffect(() => { setSel(0); }, [q, scopeAll]);

  // Internal sweep first, web research only if the platform holds nothing. The
  // server decides which of the two happened and says so.
  const runAsk = useCallback(async (term) => {
    if (!term || term.length < 2) return;
    setAsk({ loading: true });
    try {
      const token = await getToken();
      const r = await fetch(`${API_BASE}/search/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ q: term, scope: scopeAll ? 'all' : `client:${clientId}` }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'Raptor could not answer');
      setAsk({ loading: false, mode: j.mode, answer: j.answer || '', hits: j.hits || [] });
    } catch (e) {
      setAsk({ loading: false, mode: 'error', answer: e.message, hits: [] });
    }
  }, [getToken, scopeAll, clientId]);

  const go = useCallback((row) => {
    if (!row) return;
    if (row.kind === 'raptor') { runAsk(q.trim()); return; }
    onClose();
    if (row.kind === 'nav') {
      navigate(row.op && !clientId ? `/crm/${row.to}` : `/clients/${clientId}/crm/${row.to}`);
      return;
    }
    if (row.path) { navigate(row.path); return; }
    // Shared market graph rows have no client of their own, so they open the
    // matching screen in whichever workspace you are standing in.
    if (row.type_route && clientId) { navigate(`/clients/${clientId}/crm/${row.type_route}`); return; }
  }, [navigate, onClose, clientId, q, runAsk]);

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') { e.preventDefault(); if (ask) setAsk(null); else onClose(); return; }
      if (e.key === 'ArrowDown') { e.preventDefault(); setSel(s => Math.min(s + 1, Math.max(0, flat.length - 1))); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setSel(s => Math.max(0, s - 1)); return; }
      if (e.key === 'Enter') { e.preventDefault(); go(flat[sel]); }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [flat, sel, go, onClose, ask]);

  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector(`[data-i="${sel}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [sel]);

  let i = -1;
  const total = flat.length;

  return (
    <div className="crmcmd-backdrop" onClick={onClose}>
      <div className="crmcmd" onClick={e => e.stopPropagation()} role="dialog" aria-label="Search">
        <div className="crmcmd-top">
          <input
            ref={inputRef}
            className="crmcmd-input"
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search contacts, companies, replies, campaigns, the market graph"
            spellCheck={false}
          />
          {isStaff && clientId ? (
            <div className="crmcmd-scope">
              <button className={!scopeAll ? 'crmcmd-scope-on' : ''} onClick={() => setScopeAll(false)}>This workspace</button>
              <button className={scopeAll ? 'crmcmd-scope-on' : ''} onClick={() => setScopeAll(true)}>All clients</button>
            </div>
          ) : null}
        </div>

        {ask ? (
          <div className="crmcmd-answer">
            <div className="crmcmd-answerhead">
              <span>{ask.loading ? 'Raptor is looking' : ask.mode === 'internal' ? 'Found in the platform' : ask.mode === 'web' ? 'Nothing in the platform, researched on the web' : 'Could not answer'}</span>
              <button onClick={() => setAsk(null)}>Back to results</button>
            </div>
            {ask.loading
              ? <p className="crmcmd-hint">Checking every record first. If the platform holds nothing, Raptor researches it on the web, which takes a few seconds.</p>
              : <div className="crmcmd-md" data-color-mode="light"><MDEditor.Markdown source={ask.answer || ''} /></div>}
            {ask.hits && ask.hits.length ? (
              <div className="crmcmd-group">
                <div className="crmcmd-grouphead">Records</div>
                {ask.hits.map((h, n) => (
                  <button key={n} className="crmcmd-row" onClick={() => { if (h.path) { onClose(); navigate(h.path); } }}>
                    <span className="crmcmd-main">
                      <span className="crmcmd-title">{h.title}</span>
                      {h.subtitle ? <span className="crmcmd-sub">{h.subtitle}</span> : null}
                    </span>
                    <span className="crmcmd-badge">{h.type}</span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        <div className={'crmcmd-list' + (ask ? ' crmcmd-hide' : '')} ref={listRef}>
          {q.trim().length < 2 ? (
            <p className="crmcmd-hint">Type at least two characters. Results cover contacts, companies, opportunities, the inbox, campaigns, batches, deliverability, the market graph, attribution and everything in settings.</p>
          ) : null}

          {commands.length ? (
            <div className="crmcmd-group">
              <div className="crmcmd-grouphead">Go to</div>
              {commands.map(c => {
                i++;
                const k = i;
                return (
                  <button key={`nav-${c.to}`} data-i={k} className={'crmcmd-row' + (sel === k ? ' crmcmd-row--on' : '')} onMouseEnter={() => setSel(k)} onClick={() => go(c)}>
                    <span className="crmcmd-title">{c.title}</span>
                    <span className="crmcmd-badge">Screen</span>
                  </button>
                );
              })}
            </div>
          ) : null}

          {groups.map(g => (
            <div className="crmcmd-group" key={g.label}>
              <div className="crmcmd-grouphead">{g.label}</div>
              {g.rows.map(r => {
                i++;
                const k = i;
                return (
                  <button key={`${r.type}-${r.id}-${k}`} data-i={k} className={'crmcmd-row' + (sel === k ? ' crmcmd-row--on' : '')} onMouseEnter={() => setSel(k)} onClick={() => go({ kind: 'result', ...r })}>
                    <span className="crmcmd-main">
                      <span className="crmcmd-title">{r.title}</span>
                      {r.subtitle ? <span className="crmcmd-sub">{r.subtitle}</span> : null}
                    </span>
                    {r.meta ? <span className="crmcmd-badge">{r.meta}</span> : null}
                    {scopeAll && r.client_id ? <span className="crmcmd-cid">#{r.client_id}</span> : null}
                  </button>
                );
              })}
            </div>
          ))}

          {flat.some(r => r.kind === 'raptor') ? (
            <div className="crmcmd-group">
              <div className="crmcmd-grouphead">Nothing matched</div>
              {(() => {
                i++;
                const k = i;
                const row = flat[flat.length - 1];
                return (
                  <button data-i={k} className={'crmcmd-row' + (sel === k ? ' crmcmd-row--on' : '')} onMouseEnter={() => setSel(k)} onClick={() => go(row)}>
                    <span className="crmcmd-title">{row.title}</span>
                    <span className="crmcmd-badge">Raptor</span>
                  </button>
                );
              })()}
            </div>
          ) : null}

          {loading && !total ? <p className="crmcmd-hint">Searching</p> : null}
        </div>

        <div className="crmcmd-foot">
          <span>{total ? `${total} result${total === 1 ? '' : 's'}` : ''}{partial ? ', partial' : ''}</span>
          {q.trim().length >= 2 && !ask ? <button className="crmcmd-askbtn" onClick={() => runAsk(q.trim())}>Ask Raptor</button> : null}
          <span className="crmcmd-keys">
            <kbd>↑</kbd><kbd>↓</kbd> move <kbd>enter</kbd> open <kbd>esc</kbd> close
          </span>
        </div>

        {isStaff && errors.length ? (
          <div className="crmcmd-errs">{errors.length} searcher{errors.length === 1 ? '' : 's'} failed: {errors.map(e => e.type).join(', ')}</div>
        ) : null}
      </div>
    </div>
  );
}
